import { Flex, spaces } from '@electricjs/arc';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CenteredSpinner } from '@common';

type InfiniteScrollProps = {
  children: React.ReactNode;
  /** Called when we reach the end of the scrollable area and more data is allowed */
  onLoadMore: () => void;
  /** Whether there is more data to fetch (needed to prevent infinite loop) */
  hasMore: boolean;
  /** Whether you are currently loading (prevents duplicate calls) */
  isFetchingMore?: boolean;
  /** Root margin to start prefetching earlier (e.g., "600px") */
  rootMargin?: string;
  /**
   * The id of the inner element to anchor from (e.g., your <Table id="events-table" />)
   * We will find this element inside the children and compute its closest scrollable parent.
   */
  targetId: string;
};

/** Find closest scrollable parent for an element */
function getScrollParent(el: HTMLElement | null): HTMLElement | null {
  if (!el) return null;

  // climb up to find the first scrollable ancestor
  let node: HTMLElement | null = el;
  while (node) {
    const style = getComputedStyle(node);
    const overflowY = style.overflowY;
    if (
      /(auto|scroll|overlay)/.test(overflowY) &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return document.scrollingElement as HTMLElement | null;
}

/**
 * WARNING: InfiniteScroll does NOT use virtualization - all loaded rows remain in DOM.
 *          For large datasets, this can cause performance issues and memory consumption.
 *          Consider implementing virtualization if displaying thousands of rows.
 *
 * @param children - The children to render
 * @param onLoadMore - The function to call when the user scrolls to the bottom of the container
 * @param hasMore - Whether there is more data to fetch
 * @param isFetchingMore - Whether the component is fetching more data
 * @param rootMargin - The root margin to start prefetching earlier (e.g., "600px")
 * @param targetId - The id of the inner element to anchor from (e.g., your <Table id="events-table" />)
 */
export function InfiniteScroll({
  children,
  onLoadMore,
  hasMore,
  isFetchingMore = false,
  rootMargin = '400px',
  targetId,
}: InfiniteScrollProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [scrollRoot, setScrollRoot] = useState<HTMLElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Find the inner anchor and resolve the scroll root
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    let anchor: HTMLElement | null = null;

    anchor = host.querySelector(`#${targetId}`) as HTMLElement | null;

    const root = getScrollParent(anchor);
    setScrollRoot(root);
  }, [children, targetId]);

  // IntersectionObserver to trigger load more
  useEffect(() => {
    if (!scrollRoot || !sentinelRef.current) return;

    const obs = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMore && !isFetchingMore) {
          onLoadMore();
        }
      },
      { root: scrollRoot, rootMargin, threshold: 0 }
    );

    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [scrollRoot, hasMore, isFetchingMore, onLoadMore, rootMargin]);

  const { space_m } = spaces;

  return (
    <Flex width="100%" ref={hostRef} flexDirection="column">
      {children}
      {isFetchingMore && <CenteredSpinner mb={space_m} />}
      {scrollRoot &&
        (() => {
          try {
            return createPortal(
              <Flex
                ref={sentinelRef}
                aria-hidden="true"
                style={{ height: 1, width: '100%' }}
              />,
              scrollRoot
            );
          } catch (error) {
            console.warn(
              'Failed to create portal for infinite scroll sentinel:',
              error
            );
            return null;
          }
        })()}
    </Flex>
  );
}
